import {useEffect, useState} from "react";
import {schemaMatches} from "@jsonforms/core";
import {withJsonFormsControlProps} from "@jsonforms/react";
import BackupIcon from "@mui/icons-material/Backup";
import LinkIcon from "@mui/icons-material/Link";
import {Box, Button, ButtonGroup, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {getFileUploadUrl} from "../../../constants/apiEndpoints";
import {showMessage, SNACKBAR_MESSAGE_SEVERITY_ERROR} from "../../../hocs/snackbar-provider";
import ConfirmationDialog from "../../confirmation-dialog";
import FileInput, {getFileName} from "../../file-input";
import {RENDERER_TYPES} from "../constants";
import {baseUrlSelector} from "../../../state/config/configSelectors";
import {addSpinnerMessage, markSpinnerMessage} from "../../../state/spinner/spinnerActions";
import {userSelector} from "../../../state/user/userSelectors";

const spinnerUid = "custom-json-form__file-handler-render__spinner-id";
const dataStoreFilePrefix = "@@__DataStoreFile__@@";

export const FileHandlerTester = schemaMatches(schema => schema.renderer === RENDERER_TYPES.fileHandler);

export const FileHandlerRenderer = withJsonFormsControlProps(
  ({handleChange, path, data, label, required, errors, schema, config}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const baseUrl = useSelector(baseUrlSelector);
    const user = useSelector(userSelector);
    const isDataStoreFile = typeof data === "string" && data.startsWith(dataStoreFilePrefix);

    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState(schema?.options?.default ?? "link");
    const [fileName, setFileName] = useState(null);

    useEffect(() => {
      if (isDataStoreFile) {
        setMode("file");
        const fileName = data.split("/").pop();
        if (fileName) {
          setFileName(decodeURIComponent(fileName));
        }
      }
    }, [data, isDataStoreFile]);

    const handleFileImportUpload = fileData => {
      if (!fileData) {
        setFileName(null);
        handleChange(path, undefined);
      } else {
        dispatch(addSpinnerMessage(spinnerUid, t("components.customJsonForms.fileHandlerRenderer.upload.start")));

        setFileName(getFileName(fileData));

        const fileUploadUrl = config?.fileHandler?.fileUploadUrl || getFileUploadUrl();

        fetch(baseUrl + fileUploadUrl, {
          method: "POST",
          body: fileData,
          headers: {
            Authorization: user.token ? `bearer ${user.token}` : undefined
          },
          credentials: "include"
        })
          .then(res => {
            if (res.status === 413) {
              throw new Error("PAYLOAD_TOO_LARGE");
            }
            return res.json();
          })
          .then(res => {
            dispatch(markSpinnerMessage(spinnerUid));
            const filePath = res[0];
            handleChange(path, filePath);
          })
          .catch(error => {
            dispatch(markSpinnerMessage(spinnerUid, true));

            setFileName(null);
            handleChange(path, undefined);

            if (error.message === "PAYLOAD_TOO_LARGE") {
              showMessage(t("components.fileInput.messages.upload.tooLarge"), SNACKBAR_MESSAGE_SEVERITY_ERROR);
            } else {
              showMessage(t("components.fileInput.messages.upload.error"), SNACKBAR_MESSAGE_SEVERITY_ERROR);
            }
          });
      }
    };

    return (
      <Box sx={{display: "flex"}} className="custom-json-forms__renderers__file-handler">
        <ButtonGroup
          sx={{margin: "8px 8px 8px 0", maxHeight: "56px"}}
          className="custom-json-forms__renderers__file-handler__button-group"
        >
          <Button
            variant={mode === "file" ? "contained" : "outlined"}
            startIcon={<BackupIcon />}
            onClick={() => (data ? setOpen(true) : setMode("file"))}
            className="custom-json-forms__renderers__file-handler__button-file"
          >
            {t("components.customJsonForms.fileHandlerRenderer.button.file")}
          </Button>
          <Button
            variant={mode === "link" ? "contained" : "outlined"}
            startIcon={<LinkIcon />}
            onClick={() => (data ? setOpen(true) : setMode("link"))}
            className="custom-json-forms__renderers__file-handler__button-link"
          >
            {t("components.customJsonForms.fileHandlerRenderer.button.link")}
          </Button>
        </ButtonGroup>
        <ConfirmationDialog
          open={open}
          onClose={() => setOpen(false)}
          onConfirm={() => {
            setMode(mode === "file" ? "link" : "file");
            handleChange(path, undefined);
            setFileName(null);
            setOpen(false);
          }}
          title={t("components.customJsonForms.fileHandlerRenderer.confirmationDialog.title")}
          content={t("components.customJsonForms.fileHandlerRenderer.confirmationDialog.content")}
        />
        {mode === "file" ? (
          <FileInput
            fullWidth
            label={label}
            required={required}
            error={!!errors}
            helperText={errors}
            value={fileName}
            onUpload={handleFileImportUpload}
            accept={schema?.options?.accept ?? "*"}
            className="custom-json-forms__renderers__file-handler__file-input"
            enableDownload={isDataStoreFile}
            onDownload={config?.fileHandler?.onFileDownload}
          />
        ) : (
          <TextField
            fullWidth
            label={label}
            required={required}
            error={!!errors}
            helperText={errors}
            variant="outlined"
            value={data || ""}
            onChange={({target}) => handleChange(path, target.value || undefined)}
            className="custom-json-forms__renderers__file-handler__field"
          />
        )}
      </Box>
    );
  }
);
